<template>
  <FreeBottomSheet :name="name" @onOpen="handleBottomSheetOpen">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <template #default>
          <h2
            :data-qa-id="`passengersData.saved.title.${props.passengerType}`"
            class="text-text-neutral-primary text-base font-medium"
          >
            {{
              t('details.passenger.select', {
                passengerType: passengerTypeText,
              })
            }}
          </h2>
        </template>
        <template #rightAction>
          <FreeCloseButton
            data-qa-id="passengersData.saved.close"
            @click="hide"
          />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div
        v-if="isLoading"
        data-qa-id="passengersData.saved.loader"
        class="flex min-h-[80vh] items-center justify-center"
      >
        <FreeLoader class="green-spinner" />
      </div>
      <div v-else class="flex w-full flex-col items-center">
        <section class="w-full space-y-3 px-4 pb-24">
          <PassengerRadioButton
            v-for="(passenger, idx) in passengers"
            ref="savedPassengersListRef"
            :key="idx"
            :pax-idx="idx"
            :checked="isPassengerSelected(passenger)"
            :passenger="passenger"
            @onPassengerSelect="onPassengerSelect"
            @onPassengerEditClick="onPassengerEditClick(passenger, idx)"
          />
        </section>
        <div
          class="buttons-shadow bg-background-neutral-secondary fixed bottom-0 w-full rounded-t-2xl px-4 pt-4 pb-8"
        >
          <FreeButton
            data-qa-id="passengersData.saved.add"
            type="button"
            class="gray-button w-full"
            @click="onAddNewPassengerClick"
          >
            {{ t('details.passenger.addNew') }}
          </FreeButton>
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, nextTick, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { isEqual } from 'lodash-es';
  import {
    FreeBottomSheet,
    FreeButton,
    FreeCloseButton,
    FreeHeader,
    FreeLoader,
  } from 'freedom-shared';
  import type { Passenger } from '@aviata/booking-data';

  import { bookingStateModel } from '@/features/booking-state';

  import { analyticsEventsLib } from '@/shared/lib';

  import PassengerRadioButton from './PassengerRadioButton.vue';

  const props = defineProps<{
    name: string;
    passengers: Passenger[];
    passengerOnCard: Passenger | null;
    isLoading: boolean;
    passengerType: string;
    providerClass?: string;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [passenger: Passenger];
    /**
     * Emit the passenger object (not the list index) so the receiver can
     * find the correct position in `combinedPassengers` — which may be
     * larger than the filtered `visibleSavedPassengers` passed as `passengers`.
     */
    onPassengerFormOpen: [passenger: Passenger | null];
    /** Пользователь нажал «Добавить нового пассажира» — выбрать способ ввода (фото / вручную) */
    onAddNewPassenger: [];
  }>();

  const { t } = useI18n();
  const bookingStore = bookingStateModel.useBookingStore();

  const savedPassengersListRef = ref<(typeof PassengerRadioButton)[] | null>(
    null,
  );

  const localizedPassengerTypes = {
    ADT: t('passengersData.passengerTypes.ADT.text'),
    CHD: t('passengersData.passengerTypes.CHD.text'),
    INF: t('passengersData.passengerTypes.INF.text'),
    YTH: t('passengersData.passengerTypes.YTH.text'),
  };

  const passengerTypeText = computed(() => {
    return localizedPassengerTypes[
      props.passengerType as keyof typeof localizedPassengerTypes
    ];
  });

  type IdentifiablePassenger = Passenger & { id?: string };

  const isPassengerSelected = (passenger: Passenger) => {
    const selectedPax = (props.passengerOnCard as IdentifiablePassenger | null)
      ?.id;
    const candidatePax = (passenger as IdentifiablePassenger | null)?.id;
    return Boolean(selectedPax) && selectedPax === candidatePax;
  };

  const nullifyProductsInSessionStorage = () => {
    bookingStore.update({
      products: { state: null, products: null },
    });
  };

  function onPassengerSelect(passenger: Passenger) {
    if (!passenger?.firstName) {
      return;
    }

    analyticsEventsLib.selectSavedPassenger(props.providerClass);

    if (!isEqual(passenger, props.passengerOnCard)) {
      nullifyProductsInSessionStorage();
    }

    emit('onPassengerUpdate', passenger);
  }

  const onPassengerEditClick = (passenger: Passenger, idx: number) => {
    analyticsEventsLib.savedPaxEditClick({
      passenger,
      index: idx,
      providerClass: props.providerClass,
    });
    emit('onPassengerFormOpen', passenger);
  };

  const onAddNewPassengerClick = () => {
    analyticsEventsLib.addNewPaxClick(props.providerClass);
    emit('onAddNewPassenger');
  };

  const scrollToSelectedPassenger = () => {
    const idx = props.passengers.findIndex(isPassengerSelected);
    if (idx < 0) {
      return;
    }
    const card = savedPassengersListRef.value?.[idx];
    card?.$el.scrollIntoView({
      inline: 'center',
      block: 'center',
      behavior: 'smooth',
    });
  };

  const handleBottomSheetOpen = () => {
    nextTick(scrollToSelectedPassenger);
  };

  watch(
    () => props.passengerOnCard,
    () => {
      nextTick(scrollToSelectedPassenger);
    },
  );
</script>

<style scoped>
  .buttons-shadow {
    box-shadow: 0px -6px 20px 0px #0000000a;
  }
</style>
